import { StoreApi } from 'zustand';

import { History, HistoryEntry } from '@spazious/dollhouse-history';
import { applyActions, FShallowGraph, GraphAction } from '@spazious/dollhouse-shallow-graph';
import { StagingArea } from '@spazious/dollhouse-staging-area';

import { CommanderActions } from './actions/index.js';
import { ActionArgsByType, createActionFromType, historyBuildGraph } from './utils/index.js';
import {
  ActionFromType,
  ActionSideEffectsHandlers,
  Commander,
  CommanderAction,
  CommanderActionTypesEnum,
} from './types.js';

export class CommanderImpl<
  TState extends FShallowGraph = FShallowGraph,
  TAction extends CommanderAction = CommanderActions,
  TMeta = CommanderActionTypesEnum,
> implements Commander<TState, TAction, TMeta>
{
  store: StoreApi<TState>;
  history: History<GraphAction[], TMeta>;
  staging: StagingArea<GraphAction, TMeta>;
  sideEffectHandlers?: ActionSideEffectsHandlers;
  prevState: TState;

  constructor(
    store: StoreApi<TState>,
    history: History<GraphAction[], TMeta>,
    staging: StagingArea<GraphAction, TMeta>,
    sideEffectHandlers?: ActionSideEffectsHandlers
  ) {
    this.store = store;
    this.history = history;
    this.staging = staging;
    this.sideEffectHandlers = sideEffectHandlers;
    this.prevState = store.getState();

    let lastHead: HistoryEntry<GraphAction[], TMeta> | null = null;

    const updateStateFromHistory = () => {
      // A linear advance (commit, redo) only needs the new head's actions on
      // top of the live graph; any other head move (undo, checkout, load) has
      // no usable previous state and requires a full rebuild.
      const shallowGraph =
        lastHead !== null && history.head.prev === lastHead
          ? applyActions(this.prevState, history.head.state)
          : historyBuildGraph(history.head);
      lastHead = history.head;
      store.setState(shallowGraph as TState);
      this.prevState = store.getState();
    };

    staging.on('change', () => {
      const shallowGraph = applyActions(this.prevState, staging.stack);
      store.setState(shallowGraph as TState);
    });

    staging.on('commit', (stack, meta) => {
      history.push(stack, meta);
    });

    history.on('change', () => {
      updateStateFromHistory();
    });

    updateStateFromHistory();
  }

  createAction<TType extends TAction['type']>(
    type: TType,
    ...args: ActionArgsByType<TType>
  ): ActionFromType<TAction, TType> {
    return createActionFromType(this as Commander, type, ...args);
  }
}

export const createCommander = <
  TState extends FShallowGraph = FShallowGraph,
  TMeta extends CommanderActionTypesEnum = CommanderActionTypesEnum,
>(
  store: StoreApi<TState>,
  history: History<GraphAction[], TMeta>,
  staging: StagingArea<GraphAction, TMeta>,
  sideEffectHandlers?: ActionSideEffectsHandlers
) => {
  return new CommanderImpl(store, history, staging, sideEffectHandlers);
};
